import { useTheme } from '@mui/material/styles'
import { TenderStatus } from 'common/tenders/tenders.types'
import theme from 'common/utils/theme'
import { format } from 'date-fns'

import { PlanNames, Plans, Role, UserApps } from './types'

export const findInitials = (name?: string): string => {
  if (!name) {
    return ''
  }
  const words = name.split(/\s+/)
  const initials = words.map((word) => word[0]).join('')
  return ((initials[0] ?? '') + (initials[1] ?? '')).toUpperCase()
}
export const findEmailInitials = (email?: string): string => {
  if (!email) {
    return ''
  }
  const name = email.split('@')[0]
  return name.length > 1 ? name.substring(0, 2).toUpperCase() : name[0].toUpperCase()
}

export const getUserDisplayName = (user: {
  name?: string | { first?: string; last?: string }
  firstName?: string
  lastName?: string
  email: string
  name_first?: string
  name_last?: string
}): string => {
  if (user.name && typeof user.name === 'string') {
    return user.name
  } else if (user.name && typeof user.name === 'object') {
    return `${user.name?.first ?? ''} ${user.name?.last ?? ''}`
  } else if (user.firstName ?? user.lastName) {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`
  } else if (user.name_first ?? user.name_last) {
    return `${user.name_first ?? ''} ${user.name_last ?? ''}`
  }

  return user.email
}

interface ConsultantData {
  name?: {
    first?: string
    last?: string
  }
  email: string
}
export const getConsultantUsername = (user: ConsultantData): string => {
  if (user.name) {
    return `${user.name.first ?? ''} ${user.name.last ?? ''}`.trim()
  }
  return user.email
}

export const formatNumber = (value?: number | string): string => {
  if (!value) {
    return '-'
  }
  const numberValue = parseFloat(value.toString())
  if (!isNaN(numberValue)) {
    return numberValue.toLocaleString('sr-SR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  return value.toString()
}

export const getPlan = (requested: number): PlanNames | string => {
  switch (requested) {
    case Plans.basic.requestedMax:
      return PlanNames.basic
    case Plans.silver.requestedMax:
      return PlanNames.silver
    case Plans.gold.requestedMax:
      return PlanNames.gold
    default:
      return '-'
  }
}

export const getPlanColor = (planName?: PlanNames | string): string => {
  switch (planName) {
    case PlanNames.basic:
      return theme.palette.charts.indigo100.main
    case PlanNames.silver:
      return theme.palette.charts.orange400.main
    case PlanNames.gold:
      return theme.palette.charts.green100.main
    default:
      return '-'
  }
}
export const getMainAppForRole = (role: Role): string => {
  switch (role) {
    case Role.admin:
    case Role.consultant:
      return UserApps.Processing
    case Role.company:
      return UserApps.Company
    case Role.stakeholder:
      return UserApps.Reporting
    case Role.vendor:
      return UserApps.Vendors
    default:
      return UserApps.Company
  }
}

export const formatNumberNoDec = (value: number | string): string => {
  const numberValue = parseFloat(value.toString())
  if (!isNaN(numberValue)) {
    return numberValue.toLocaleString('sr-SR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }
  return value.toString()
}

export const getTenderStatus = (
  value: string,
  config: { proposalSubmissionDeadlinePassed?: boolean } = {},
): string => {
  switch (value) {
    case TenderStatus.canceled:
      return 'Otkazan'
    case TenderStatus.draft:
      return 'Skica'
    case TenderStatus.submitted:
      return config.proposalSubmissionDeadlinePassed ? 'Biranje ponuda' : 'Čekanje ponude'
    case TenderStatus.finished:
      return 'Završen'
    case TenderStatus.finishedNothingSelected:
      return 'Završen bez odabira'
    default:
      return '-'
  }
}

export const getTenderStatusColor = (
  value: string,
  config: { proposalSubmissionDeadlinePassed?: boolean } = {},
): string => {
  const { palette } = useTheme()

  switch (value) {
    case TenderStatus.canceled:
      return palette.charts.red200.main
    case TenderStatus.draft:
      return palette.blue100.main
    case TenderStatus.submitted:
      return config.proposalSubmissionDeadlinePassed
        ? palette.charts.purple100.main
        : palette.charts.orange400.main
    case TenderStatus.finished:
      return palette.green.main
    case TenderStatus.finishedNothingSelected:
      return palette.charts.orange400.main
    default:
      return palette.charts.purple100.main
  }
}

export function formatDate(inputDate: string | undefined): string {
  if (!inputDate) {
    return ''
  }

  const date = new Date(inputDate)
  return format(date, 'dd.MM.yyyy.')
}
